import { ChevronDown, Search } from 'lucide-preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

export interface HeaderFilterOption<T> {
  key: string;
  label: string;
  searchText?: string;
  value: T;
}

interface HeaderFilterSelectProps<T> {
  ariaLabel: string;
  valueLabel: string;
  options: HeaderFilterOption<T>[];
  selectedKey?: string;
  disabled?: boolean;
  searchable?: boolean;
  onSelect: (value: T) => void | Promise<void>;
}

export function HeaderFilterSelect<T>({
  ariaLabel,
  valueLabel,
  options,
  selectedKey,
  disabled = false,
  searchable = false,
  onSelect,
}: HeaderFilterSelectProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const visibleOptions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return options;

    return options.filter((option) =>
      (option.searchText || option.label).toLowerCase().includes(normalizedSearch),
    );
  }, [options, searchTerm]);

  const handleSelect = (option: HeaderFilterOption<T>) => {
    setIsOpen(false);
    setSearchTerm('');
    void onSelect(option.value);
  };

  return (
    <div className="header-filter-select" ref={containerRef}>
      <button
        type="button"
        className={`header-filter-select__button${isOpen ? ' is-open' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled || options.length === 0}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
      >
        <span className="header-filter-select__value">{valueLabel}</span>
        <ChevronDown size={15} className="header-filter-select__chevron" aria-hidden="true" />
      </button>

      {isOpen && options.length > 0 && (
        <div className="header-filter-select__menu">
          {searchable && (
            <div className="header-filter-select__search">
              <Search size={15} aria-hidden="true" />
              <input
                type="search"
                value={searchTerm}
                placeholder={`Search ${ariaLabel.toLowerCase()}…`}
                aria-label={`Search ${ariaLabel.toLowerCase()}`}
                onInput={(event) => setSearchTerm(event.currentTarget.value)}
                autoFocus
              />
            </div>
          )}

          <div className="header-filter-select__options">
            {visibleOptions.length > 0 ? (
              visibleOptions.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  className={`header-filter-select__option${
                    option.key === selectedKey ? ' is-selected' : ''
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <p className="header-filter-select__empty">No results found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
